import {createSelector} from '@reduxjs/toolkit';
import createCachedSelector from 're-reselect';
import {RootState} from '../../rootReducer';
import {meetingRoomAdapter} from './adapters';
import {dateCompareAsc, groupBy, nonEmpty, numericCompareDesc} from '@lib/utils';
import {isAboveRequiredScoreThreshold} from './helpers';
import {parseISO} from 'date-fns';

export const {
  selectById: getMeetingRoomById,
  selectIds: getMeetingRoomIds,
  selectEntities: getMeetingRoomEntities,
  selectAll: getAllMeetingRooms,
  selectTotal: getTotalMeetingRooms,
} = meetingRoomAdapter.getSelectors((state: RootState) => state.data.meetingRooms.meetingRooms);

export const getCapacityFilteredAvailableMeetingRoomsLoadingStatus = (state: RootState) =>
  state.data.meetingRooms.capacityFilteredAvailableMeetingRoomsLoadingStatus;

export const getCapacityFilteredAvailableMeetingRooms = createSelector(
  (state: RootState) => state.data.meetingRooms.capacityFilteredAvailableMeetingRoomIds,
  getMeetingRoomEntities,
  (meetingRoomIds, meetingRoomEntities) => nonEmpty(meetingRoomIds.map((roomId) => meetingRoomEntities[roomId])),
);

export const getAvailableMeetingRoomLoadingStatus = (state: RootState) =>
  state.data.meetingRooms.availableMeetingRoomLoadingStatus;

export const getAvailableMeetingRooms = createSelector(
  (state: RootState) => state.data.meetingRooms.availableMeetingRoomIds,
  getMeetingRoomEntities,
  (meetingRoomIds, meetingRoomEntities) => nonEmpty(meetingRoomIds.map((roomId) => meetingRoomEntities[roomId])),
);

export const getMeetingRoomsGroupedByBuilding = createCachedSelector(
  (state: RootState) => state.data.meetings.items.entities,
  getMeetingRoomEntities,
  (_state: RootState, meetingId: string) => meetingId,
  (meetings, meetingRooms, meetingId) => {
    const meeting = meetings[meetingId];

    const rooms = (meeting?.meetingRooms ?? []).map((roomId) => meetingRooms[roomId]);
    const roomsInABuilding = nonEmpty(rooms).filter((meetingRoom) => !!meetingRoom.buildingId);
    const groupedRooms = groupBy(roomsInABuilding, (room) => room.buildingId);
    return groupedRooms;
  },
)((_state, meetingId) => meetingId);

export const getMeetingRoomsByMeetingId = createCachedSelector(
  (state: RootState) => state.data.meetings.items.entities,
  getMeetingRoomEntities,
  (_state: RootState, meetingId: string) => meetingId,
  (meetings, meetingRooms, meetingId) => {
    const meeting = meetings[meetingId];
    if (!meeting) {
      return [];
    }
    return nonEmpty(meeting.meetingRooms.map((roomId) => meetingRooms[roomId]));
  },
)((_state, meetingId) => meetingId);

export const getSelectedMeetingRoom = createSelector(
  (_state: RootState, meetingRoomId: string) => meetingRoomId,
  getMeetingRoomEntities,
  (meetingRoomId, meetingRooms) => {
    if (!meetingRoomId || !meetingRooms) {
      return;
    }

    return meetingRooms[meetingRoomId];
  },
);

export const getMeetingRoomSuggestionByBuildingId = createSelector(
  (state: RootState, _buildingId: string) => state.data.meetingRooms.meetingRoomSuggestionsByBuilding,
  (_state: RootState, buildingId: string) => buildingId,
  (suggestionsByBuilding, buildingId) => suggestionsByBuilding[buildingId],
);

export const getMeetingRoomSuggestionByBuildingIdLoadingStatus = createSelector(
  getMeetingRoomSuggestionByBuildingId,
  (suggestionData) => suggestionData?.status,
);

export const getMeetingRoomSuggestionDataById = createSelector(
  (_state: RootState, _buildingId: string, suggestionId: string) => suggestionId,
  getMeetingRoomSuggestionByBuildingId,
  (suggestionId, suggestions) => suggestions?.data?.[suggestionId],
);

export const getAllMeetingRoomSuggestionsUnfiltered = createSelector(
  getMeetingRoomEntities,
  getMeetingRoomSuggestionByBuildingId,
  (meetingRoomEntities, suggestion) => {
    if (!suggestion) return [];

    const {meetingRoomIds, data: suggestionEntities} = suggestion;
    const allValidRoom = nonEmpty(meetingRoomIds.map((roomId) => meetingRoomEntities[roomId]));

    return allValidRoom.sort((a, b) =>
      numericCompareDesc(suggestionEntities[a.id]!.score, suggestionEntities[b.id]!.score),
    );
  },
);

export const getAllMeetingRoomSuggestions = createSelector(
  getMeetingRoomEntities,
  getMeetingRoomSuggestionByBuildingId,
  (meetingRoomEntities, suggestion) => {
    if (!suggestion) return [];

    const {meetingRoomIds, data: suggestionEntities} = suggestion;
    const allValidRoom = nonEmpty(meetingRoomIds.map((roomId) => meetingRoomEntities[roomId]));

    return allValidRoom
      .filter((room) => {
        const suggestionData = suggestionEntities[room.suggestionData ?? ''];
        return !suggestionData?.markedAsBookedLocally && isAboveRequiredScoreThreshold(suggestionData?.score);
      })
      .sort((a, b) => numericCompareDesc(suggestionEntities[a.id]!.score, suggestionEntities[b.id]!.score));
  },
);

export const getSuggestionDataForRoomId = createSelector(
  (state: RootState, _buildingId: string, roomId: string) => getMeetingRoomById(state, roomId),
  getMeetingRoomSuggestionByBuildingId,
  (room, suggestionData) => suggestionData?.data?.[room?.suggestionData ?? ''],
);

export const getRoomScheduleInformationForRoomId = createSelector(getSuggestionDataForRoomId, (suggestionData) => {
  const {scheduleInformation} = suggestionData ?? {};
  if (!scheduleInformation?.length) return scheduleInformation;

  return [...scheduleInformation].sort((a, b) => dateCompareAsc(parseISO(a.start), parseISO(b.start)));
});

export const getMeetingRoomAmenitiesByBuilding = (rootState: RootState) =>
  rootState.data.meetingRooms.buildingMeetingRoomAmenities;
